import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import MessageTail from '../shared/MessageTail'
import Loader from '../shared/Loader'
import { useQueryGetUserMe } from '../../../../../queries/userQueries'


const AnimatedMessageContent = ({ content, isLastMessage, isLoadingBot }) => {
  const [displayedText, setDisplayedText] = useState(() => {
    return isLastMessage && isLoadingBot ? '' : content
  })
  const indexRef = useRef(isLastMessage && isLoadingBot ? 0 : content.length)
  const fullTextRef = useRef(content)

  useEffect(() => {
    if (!isLastMessage || !isLoadingBot) {
      setDisplayedText(content)
      fullTextRef.current = content
      indexRef.current = content.length
      return
    }

    fullTextRef.current = content

    if (indexRef.current > content.length) {
      indexRef.current = 0
      setDisplayedText('')
    }

    const timer = setInterval(() => {
      if (indexRef.current < fullTextRef.current.length) {
        setDisplayedText(fullTextRef.current.slice(0, indexRef.current + 1))
        indexRef.current += 1
      } else {
        clearInterval(timer)
      }
    }, 15)

    return () => clearInterval(timer)
  }, [content, isLastMessage, isLoadingBot])

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        a: ({ node, ...props }) => (
          <a
            {...props}
            className="text-accent2 underline"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation()
            }}
          />
        ),
        table: ({ node, ...props }) => (
          <div className="my-4 overflow-x-auto no-scrollbar rounded-xl border border-accent2/25 bg-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
            <table className="w-full min-w-[480px] table-auto border-collapse" {...props} />
          </div>
        ),
        th: ({ node, ...props }) => (
          <th className="border-b border-accent2/25 px-4 py-3 bg-accent2/[0.09] text-text text-left text-sm font-semibold" {...props} />
        ),
        td: ({ node, ...props }) => (
          <td className="border-b border-accent2/20 px-4 py-3 text-sm" {...props} />
        )
      }}
    >
      {displayedText}
    </ReactMarkdown>
  )
}

const Messages = ({ messages, isLoading }) => {
  const { data: userMe } = useQueryGetUserMe()
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom();
  }, [messages])

  const getFileName = (fileUrl) => {
    const splittedFileName = fileUrl.split('/').pop().split('-')

    if (splittedFileName.length > 5) {
      return splittedFileName.slice(5).join('-')
    }

    return splittedFileName.join('-')
  }

  const hasTable = (content) => {
    if (!content) return false
    const hasHtmlTable = content.includes('<table') || content.includes('</table>')
    if (hasHtmlTable) return true

    const lines = content.split('\n')
    const tableLines = lines.filter(line => {
      const pipeCount = (line.match(/\|/g) || []).length
      return pipeCount >= 2 && line.trim().startsWith('|')
    })
    return tableLines.length >= 2
  }

  const getWelcomeMessage = () => {
    const userName = userMe?.firstname || 'Usuario'
    return `Hola, ${userName}. Soy LUCA, tu asistente financiero. ¿En qué te puedo ayudar hoy?`
  }

  const welcomeMessage = messages.length === 0 ? {
    id: 'welcome-message',
    content: getWelcomeMessage(),
    isFromConsumer: false,
    isnotcostumer: true,
    createdAt: new Date().toISOString()
  } : null

  const allMessages = welcomeMessage ? [welcomeMessage, ...messages] : messages

  return (
    <div className="flex flex-col overflow-x-hidden overflow-y-auto px-1.5 sm:px-4">
      {allMessages.map((message, index) => {
        const isFromConsumer = !!message.isFromConsumer
        const isNotCustomer = !!message.isnotcostumer || !!message.isNotCustomer
        const containsTable = hasTable(message.content)
        const isTemporary = message.id && message.id.startsWith('temp-')
        const bubbleClass = isFromConsumer
          ? 'bg-primary text-white'
          : isNotCustomer
          ? 'bg-gradient-to-br from-white via-white to-accent2/[0.06] text-text border border-accent2/20'
          : 'bg-gradient-to-br from-white via-white to-accent2/[0.06] text-text border border-accent2/20'
        return (
          <div
            key={message.id || index}
            className={`flex ${isFromConsumer ? 'justify-end' : 'justify-start'} mb-3 ${isTemporary ? 'opacity-0 animate-fade-in' : ''}`}
          >
            <div
              className={`relative min-w-0 break-words px-4 py-3 text-[13px] sm:text-[15px] ${
                containsTable ? 'w-full max-w-full sm:max-w-[92%]' : 'max-w-[88%] sm:max-w-[78%]'
              } ${bubbleClass}`}
              style={{
                borderRadius: '16px',
                ...(isFromConsumer ? {
                  borderBottomRightRadius: '4px'
                } : {
                  borderBottomLeftRadius: '4px'
                }),
                position: 'relative',
                boxShadow: isFromConsumer
                  ? '0 16px 30px rgba(0, 0, 51, 0.18)'
                  : '0 14px 28px rgba(15, 23, 42, 0.08)'
              }}
            >
              <MessageTail isFromConsumer={isFromConsumer} />
              <div>
                <div className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] sm:text-[11px] ${
                  isFromConsumer ? 'text-white/70' : 'text-accent2'
                }`}>
                  {isFromConsumer ? 'Tú' : 'LUCA'}
                </div>
                {isFromConsumer ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          className="text-blue-200 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                        />
                      ),
                      table: ({ node, ...props }) => (
                        <div className="my-4 overflow-x-auto no-scrollbar rounded-xl border border-accent2/25 bg-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                          <table className="w-full min-w-[480px] table-auto border-collapse" {...props} />
                        </div>
                      ),
                      th: ({ node, ...props }) => (
                        <th className="border-b border-accent2/25 px-4 py-3 bg-white/10 text-white text-left text-sm font-semibold" {...props} />
                      ),
                      td: ({ node, ...props }) => (
                        <td className="border-b border-accent2/20 px-4 py-3 text-sm" {...props} />
                      )
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                ) : (
                  <AnimatedMessageContent
                    content={message.content}
                    isLastMessage={index === allMessages.length - 1}
                    isLoadingBot={isLoading}
                  />
                )}
                {message.files && message.files.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-semibold">Archivos adjuntos:</p>
                    <ul className="list-disc pl-5">
                      {message.files.map((fileUrl, fileIndex) => (
                        <li key={fileIndex} className="text-sm">
                          {getFileName(fileUrl)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
      {isLoading && (
        <div className="mb-3 flex justify-start">
          <div
            className="relative max-w-[88%] rounded-2xl rounded-bl-md border border-accent2/20 bg-gradient-to-br from-white via-white to-accent2/[0.06] px-4 py-3 text-[13px] text-text shadow-card sm:max-w-[78%] sm:text-[15px]"
            style={{
              position: 'relative',
              boxShadow: '0 14px 28px rgba(15, 23, 42, 0.08)'
            }}
          >
            <MessageTail isFromConsumer={false} />
            <div className='mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent2 sm:text-[11px]'>
              LUCA
            </div>
            <Loader size='md' />
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  )
}

export default Messages
